import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';
import { useDispatch, useSelector } from 'react-redux';
import HeaderText from '../../../shared/components/HeaderText/HeaderText';
import Text from '../../../shared/components/Text/Text';
import { startImportPolling, resetImport, fetchPendingImports } from '../ducks';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Overlay = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  animation: ${fadeIn} 0.5s ease forwards;
`;

const Card = styled('div')`
  width: 100%;
  max-width: 470px;
  background: #FFFFFF;
  border: 1px solid #EEEEEE;
  border-radius: 16px;
  box-shadow:
    0 0 0.5px 0 rgba(48, 49, 51, 0.05),
    0 16px 12px 0 rgba(48, 49, 51, 0.10);
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 32px;
  box-sizing: border-box;

  @media (max-width: 600px) {
    padding: 20px;
    gap: 24px;
    border-radius: 12px;
  }
`;

const TopSection = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  width: 100%;
`;

const TitleBlock = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  text-align: center;
  width: 100%;
`;

const Title = styled(HeaderText)`
  font-size: 24px;
  font-weight: 900;
  margin: 0;
  letter-spacing: 0;
  line-height: 1.5;
  width: 100%;

  @media (max-width: 600px) {
    font-size: 20px;
  }
`;

const TimeHint = styled(Text)`
  font-size: 16px;
  font-weight: 400;
  color: #9E9E9E;
  margin: 0;
  line-height: 1.5;
  width: 100%;
`;

const Divider = styled('div')`
  height: 1px;
  background: #EEEEEE;
  width: 100%;
`;

const StatusRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
`;

const SpinnerWrap = styled('span')`
  display: inline-flex;
  width: 24px;
  height: 24px;
  animation: ${spin} 1.4s linear infinite;
  color: #0DCC8A;
`;

const StatusMessage = styled(({ visible, ...rest }) => <span {...rest} />)`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  font-style: italic;
  color: #0DCC8A;
  line-height: 1.5;
  text-align: center;
  white-space: nowrap;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

const NextStepBox = styled('div')`
  background: #FCFCFC;
  border-radius: 4px;
  padding: 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`;

const NextStepContent = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  color: #444444;
  font-size: 16px;
  line-height: 1.5;
`;

const NextStepTitle = styled(HeaderText)`
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  line-height: 1.5;
  width: 100%;
`;

const NextStepBody = styled(({ visible, ...rest }) => <Text {...rest} />)`
  font-size: 16px;
  font-weight: 400;
  margin: 0;
  line-height: 1.5;
  width: 100%;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

// Slow-import state: the backend job is still running past our 5-min client
// poll window. The work continues server-side, so we steer the coach to the
// drafts list rather than leaving them on a spinner.
const SlowBody = styled(Text)`
  font-size: 16px;
  font-weight: 400;
  margin: 0;
  line-height: 1.5;
  width: 100%;
  text-align: left;
`;

const PrimaryButton = styled('button')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #FFFFFF;
  background: #0DCC8A;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  cursor: pointer;
  width: 100%;
  transition: transform 0.1s ease, opacity 0.15s ease;

  &:hover { opacity: 0.92; transform: translateY(-1px); }
  &:active { transform: translateY(0); }
`;

// Paired status + what's-next bodies. Both halves of each entry crossfade
// together so the coach sees a consistent "stage" rather than the status
// and the body drifting independently. The import runs server-side without
// progress events, so this is purely cadence to signal liveness.
const STATUS_STAGES = [
  {
    status: 'Working on it...',
    nextBody:
      'After we process your spreadsheet, next we’ll ask you to check over '
      + 'the exercises to make sure we matched them correctly.',
  },
  {
    status: 'Analyzing spreadsheet…',
    nextBody:
      'Your spreadsheet will be imported as a Saved Program. You can assign it '
      + 'as a self-paced workout, or load it into a calendar to give it a start '
      + 'date and make it a scheduled workout.',
  },
];

// Stroke uses currentColor so the parent's color reaches it.
const SegmentedSpinner = () => (
  <svg viewBox='0 0 24 24' width='100%' height='100%' fill='none'>
    <g stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
      <line x1='12' y1='2' x2='12' y2='5' opacity='1' />
      <line x1='19.07' y1='4.93' x2='16.95' y2='7.05' opacity='0.85' />
      <line x1='22' y1='12' x2='19' y2='12' opacity='0.7' />
      <line x1='19.07' y1='19.07' x2='16.95' y2='16.95' opacity='0.55' />
      <line x1='12' y1='22' x2='12' y2='19' opacity='0.4' />
      <line x1='4.93' y1='19.07' x2='7.05' y2='16.95' opacity='0.3' />
      <line x1='2' y1='12' x2='5' y2='12' opacity='0.25' />
      <line x1='4.93' y1='4.93' x2='7.05' y2='7.05' opacity='0.2' />
    </g>
  </svg>
);

const StepProcessing = () => {
  const dispatch = useDispatch();
  const [stageIndex, setStageIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  const importHistoryId = useSelector((state) => state.aiProgramImport.importHistoryId);
  const isSlowImport = useSelector((state) => state.aiProgramImport.isSlowImport);

  // Poll the extract job once we have its history id (set by the 202 from
  // extractProgram). The thunk returns a cancel fn — tying it to this effect
  // means the loop stops the moment we leave this screen (Review on success,
  // Detect on failure, or unmount), so no zombie polls or stray transitions.
  useEffect(() => {
    if (!importHistoryId) return undefined;
    const cancel = dispatch(startImportPolling(importHistoryId));
    return cancel;
  }, [dispatch, importHistoryId]);

  // Browser-close guard — only while we're actively waiting on the job. Closing
  // the tab won't cancel the server-side work, but the coach would lose the
  // review step. Once we're in the slow state we've already pointed them at the
  // drafts list, so there's nothing left to lose; drop the guard then.
  useEffect(() => {
    if (isSlowImport) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isSlowImport]);

  const handleBackToImports = () => {
    // Refresh drafts so the import appears the moment the job finishes, then
    // drop back to the uploader (resetImport preserves quota + pendingImports).
    dispatch(fetchPendingImports());
    dispatch(resetImport());
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
    }, 12000);
    return () => clearTimeout(timer);
  }, [stageIndex]);

  useEffect(() => {
    if (!visible) {
      const timer = setTimeout(() => {
        setStageIndex((prev) => (prev + 1) % STATUS_STAGES.length);
        setVisible(true);
      }, 400);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [visible]);

  const stage = STATUS_STAGES[stageIndex];

  if (isSlowImport) {
    return (
      <Overlay>
        <Card>
          <TopSection>
            <TitleBlock>
              <Title>This is taking a while</Title>
              <TimeHint>Your import is still being processed.</TimeHint>
            </TitleBlock>
            <Divider />
          </TopSection>

          <NextStepBox>
            <NextStepContent>
              <NextStepTitle as='h2'>What&rsquo;s next?</NextStepTitle>
              <SlowBody>
                We&rsquo;re still working on your spreadsheet. Check back in a
                few minutes — once it&rsquo;s done it&rsquo;ll appear under
                &ldquo;Past imports waiting for review.&rdquo; If it doesn&rsquo;t
                show up, just try the import again.
              </SlowBody>
            </NextStepContent>
          </NextStepBox>

          <PrimaryButton type='button' onClick={handleBackToImports}>
            Back to imports
          </PrimaryButton>
        </Card>
      </Overlay>
    );
  }

  return (
    <Overlay>
      <Card>
        <TopSection>
          <TitleBlock>
            <Title>Processing</Title>
            <TimeHint>This usually takes 1-3 minutes.</TimeHint>
          </TitleBlock>

          <Divider />

          <StatusRow>
            <SpinnerWrap>
              <SegmentedSpinner />
            </SpinnerWrap>
            <StatusMessage visible={visible}>
              {stage.status}
            </StatusMessage>
          </StatusRow>
        </TopSection>

        <NextStepBox>
          <NextStepContent>
            <NextStepTitle as='h2'>What&rsquo;s next?</NextStepTitle>
            <NextStepBody visible={visible}>
              {stage.nextBody}
            </NextStepBody>
          </NextStepContent>
        </NextStepBox>
      </Card>
    </Overlay>
  );
};

export default StepProcessing;
